// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@tps/App/Components/Notify/Notify";
import CartLineQuantity, { CartLineQuantityStyles } from "@tps/Overrides/Widgets/Cart/CartLineQuantity";
import IconTrashInactive from "@tps/XDL/Icons/IconTrashInactive";
import { color, ellipsis } from "@tps/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    productPreviousPurchaseStyles?: TypographyPresentationProps;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    brand?: LinkPresentationProps;
    nameText?: TypographyPresentationProps;
    lineDescription?: LinkPresentationProps;
    qtyAndPriceWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceBranchWrapper?: InjectableCss;
    cartLineTotal?: TypographyPresentationProps;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    // webDiscountMessage?: TypographyPresentationProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    branchName?: TypographyPresentationProps;
}

const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            padding: 12px 16px;
            &:not(:last-child) {
                &::after {
                    content: "";
                    position: absolute;
                    display: block;
                    bottom: 0;
                    width: calc(100% - 32px);
                    border-bottom: 1px solid ${color("common.border")};
                }
            }
        `,
    },
    imageWrapper: {
        css: css`
            overflow: hidden;
            margin-right: 12px;
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
        `,
    },
    productPreviousPurchaseStyles: {
        css: css`
            margin-top: auto;
        `,
    },
    cartLineImage: {
        width: "80px",
        height: "80px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: calc(100% - 114px);
        `,
    },
    lineDescription: {
        color: "text",
        typographyProps: {
            size: 14,
            weight: "bold",
            lineHeight: "16px",
            css: css`
                letter-spacing: 0;
                ${ellipsis(2)}
            `,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: start;
            flex-wrap: nowrap;
            justify-content: space-between;
            margin-top: auto;
        `,
    },
    qtyWrapper: {
        css: css`
            margin-right: 5px;
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    cartLineQuantityStyles: {
        qtyFieldStyles: {
            wrapper: {
                css: css`
                    min-width: 94px;
                    min-height: 28px;
                `,
            },
        },
    },
    extendedPriceBranchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            text-align: right;
        `,
    },
    cartLineTotal: {
        variant: "h5",
        forwardAs: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    nameText: {
        size: 12,
        weight: "bold",
        lineHeight: "17px",
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: IconTrashInactive,
                size: 16,
                color: "common.ui4",
                css: css`
                    margin-right: 5px;
                    position: absolute;
                    right: 10px;
                    top: 14px;
                `,
            },
        },
        css: css`
            margin-left: auto;
            &&&& {
                outline-style: none;
            }
        `,
    },
    // webDiscountMessage: {
    //     size: 12,
    //     lineHeight: "17px",
    //     weight: "bold",
    //     css: css`
    //         margin-top: 4px;
    //     `,
    // },
    branchName: {
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-top: 4px;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            css: css`
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
                &:has(> div > div:empty) {
                    display: none;
                }
            `,
        },
        errorWrapper: {
            css: css`
                margin-bottom: 12px;
                &:empty {
                    display: none;
                }
            `,
        },
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    // const webOrderDiscount = useAppSelector(
    //     state => (getSettingsCollection(state).orderCustomSettings as any)?.webOrderDiscount || "",
    // );
    // const unitNetPriceDiscount = cartLine.properties?.totalExtendedUnitNetPriceDiscount;
    const branchName = JSON.parse(cartLine.properties?.warehouse || "null")?.["Description"];

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy}>
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <Link
                        onClick={() => removeCartLineClickHandler(cartLine)}
                        data-test-selector="cartline_removeLine"
                        {...styles.removeCartLineLink}
                    />
                    <StyledWrapper>
                        {cartLine.brand &&
                            (cartLine.brand.detailPagePath ? (
                                <Link
                                    href={cartLine.brand.detailPagePath}
                                    onClick={hideTippy}
                                    {...styles.brand}
                                    aria-label={cartLine.brand.name}
                                >
                                    {cartLine.brand.name}
                                </Link>
                            ) : (
                                <Typography {...styles.nameText}>{cartLine.brand.name}</Typography>
                            ))}
                        <Link href={cartLine.productUri} onClick={hideTippy} {...styles.lineDescription}>
                            {cartLine.shortDescription}
                        </Link>
                    </StyledWrapper>
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart?.failedToGetRealTimeInventory}
                        extendedStyles={styles.productAvailabilityStyles}
                    />
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.qtyWrapper}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                }}
                            />
                        </StyledWrapper>
                        <StyledWrapper {...styles.extendedPriceBranchWrapper}>
                            {!cart?.cartNotPriced && cartLine && cartLine.pricing && (
                                <Typography {...styles.cartLineTotal}>
                                    {cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                            )}
                            {/* {webOrderDiscount && (
                                <Typography {...styles.webDiscountMessage}>
                                    {`Web Order (${Number(webOrderDiscount) * 100}%) `}
                                    {unitNetPriceDiscount && (
                                        <Typography size={12} color="success">
                                            &nbsp;&nbsp;{` - ${unitNetPriceDiscount}`}
                                        </Typography>
                                    )}
                                </Typography>
                            )} */}
                            {branchName && (
                                <Typography {...styles.branchName}>
                                    <b>{translate("Branch: ")}</b>
                                    {branchName}
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
